import { useState } from 'react';
import ApiDatabase from 'server';
import { canConfirmJustificatif } from 'components/justificatifConfirmation/JustificatifConfirmation';

/**
 * The confirmation step a company setting has to pass through (JBWEB-1214).
 *
 * Holds the file and the attestation, and uploads only once the admin
 * confirms — so closing the modal beforehand stores nothing, which is what
 * separates this from a form that uploads on selection.
 *
 * The save runs after the upload and receives the stored document, so an
 * upload that fails leaves the setting untouched: a change recorded without
 * its justificatif is exactly what this step exists to prevent.
 */
const useJustificatifConfirmation = () => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [file, setFile] = useState(null);
  const [isAttested, setIsAttested] = useState(false);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const reset = () => {
    setIsConfirming(false);
    setFile(null);
    setIsAttested(false);
    setError(null);
    setIsUploading(false);
  };

  /**
   * @param save  called with the stored document once it is in place
   */
  const confirm = (save) => {
    if (!canConfirmJustificatif(file, isAttested) || isUploading) return;

    setIsUploading(true);
    setError(null);

    const body = new FormData();
    body.append('justificatif', file);

    ApiDatabase.postCompanyJustificatif(
      body,
      (data) => {
        setIsUploading(false);
        save(data.justificatif);
      },
      (err) => {
        setIsUploading(false);
        if (err.name === 'CanceledError') return;
        // The API re-reads the file's real type and size; its refusal is the
        // one that counts, so it is shown rather than a generic failure.
        setError(
          err?.response?.data?.message ??
            "Le justificatif n'a pas pu être enregistré."
        );
      }
    );
  };

  return {
    isConfirming,
    setIsConfirming,
    file,
    setFile,
    isAttested,
    setIsAttested,
    error,
    isUploading,
    canConfirm: canConfirmJustificatif(file, isAttested),
    confirm,
    reset,
  };
};

export default useJustificatifConfirmation;
